import type { NextApiResponse, NextPageContext } from 'next';
import type { NextRouter } from 'next/router';
import Router from 'next/router';

import { logger } from 'Services/logger/logger';
import { ROUTES } from 'Utilities/routes';

type RouteConfig = { as?: string; path?: string; replace?: boolean; shallow?: boolean };

type DestinationPath = string | string[] | undefined;

export const isDestinationSameAsOrigin = (destinationPath: string, baseURI: string = document.baseURI): boolean =>
	new URL(baseURI).origin === new URL(destinationPath, baseURI).origin;

export const validateRedirectDestination = (
	destinationPath: DestinationPath,
	baseURI: string = document.baseURI,
): { valid: boolean } => ({
	valid: typeof destinationPath === 'string' && isDestinationSameAsOrigin(destinationPath, baseURI),
});

export const createBrowserOnlyRedirectPath = (
	destinationPath: DestinationPath,
	fallback: string = ROUTES.INDEX.href,
	options: { baseURI?: string; hostURL?: string } = {},
): string => {
	const baseURI = options.baseURI ?? document.baseURI;
	const hostURL = options.hostURL ?? `${window.location.protocol}//${window.location.host}`;
	const resolvedFallback = validateRedirectDestination(fallback, baseURI).valid ? fallback : ROUTES.INDEX.href;

	const { valid } = validateRedirectDestination(destinationPath, baseURI);

	return `${hostURL}${valid ? destinationPath : resolvedFallback}`;
};

export const isomorphicRedirect = (ctx: NextPageContext, path: string): void => {
	try {
		if (ctx.isSSR) {
			(ctx.res as NextApiResponse).redirect(307, path);
		} else {
			void Router.replace(path);
		}
	} catch (error) {
		logger.error({
			context: {
				extra: {
					stringifiedError: JSON.stringify(error),
					to: path,
				},
			},
			error: error instanceof Error ? error : new Error('Failed to redirect'),
			message: 'Failed to redirect',
			scope: 'isomorphicRedirect',
		});
	}
};

export const routeWithErrorHandling = async (router: NextRouter, routeConfig: RouteConfig) => {
	const { path, as = path, shallow: isShallow = true, replace = false } = routeConfig;
	const logScope = 'routeWithErrorHandling';
	// For static pages, we need full page transitions (shallow: false) so getInitialProps runs
	const shallow = path === ROUTES.STATIC_PAGE.href || path === ROUTES.INDEX.href ? false : isShallow;

	if (!path) {
		logger.warn({ message: 'No path provided to navigate to', scope: logScope });
		return;
	}

	const routerMethod = replace ? router.replace.bind(router) : router.push.bind(router);

	await routerMethod(path, as, { shallow }).catch((error) => {
		logger.error({ error, message: `Error navigating to path: ${path}`, scope: logScope });
	});
};
